{
  "title": "How to Calculate, Lock, and Publish Results",
  "description": "Calculate scores for a round (and optionally a track), review manually, then lock and publish.",
  "headings": [
    "What You'll Accomplish",
    "Applies To",
    "Before You Start",
    "Steps",
    "What You'll See",
    "FAQ"
  ],
  "summary": "When you're done, results are published, participants can see their scores and rankings, and the published result can no longer be changed.",
  "roleNote": "Competition Admin",
  "prerequisites": [
    "Judges have completed scoring for this round",
    "You've confirmed the correct rubric version was used"
  ],
  "pathLabel": "Organizer Dashboard → My Competition → Results",
  "steps": [
    {
      "title": "1. Calculate results",
      "description": "Choose a round and (optionally) a track, then click \"Calculate Results\". A risk-warning message appears first (only submissions with at least 2 judges' locked scores count; anything below that will be listed as \"missing a qualifying score\"), and you click \"Confirm calculate\" to actually create the result run. A submission needs at least 2 judges' valid, locked scores to be included in the ranking — submissions below that threshold are listed as excluded, not as a calculation failure. If a judge is recorded as having a conflict of interest after their score was locked, that score is retroactively excluded from this calculation."
    },
    {
      "title": "2. Manual review",
      "description": "After calculation the result status is \"Calculated\", and the card shows this run's full metadata (Run ID, calculation time, who calculated it, included/missing score counts with team names, any tie signal) — check those numbers first, then review individual submissions and adjust their award tier (with a reason) as needed, best done before locking."
    },
    {
      "title": "3. Lock results",
      "description": "Once review is done, click \"Lock\". An impact-explanation message appears first (after locking you can still adjust award tiers (with a reason) until you publish, and only a published run is final; locking doesn't affect other result runs and the run is never deleted); confirm to actually lock. Status moves to \"Locked\" and the card additionally shows who locked it. Locking and publishing are independent, separately audited actions — locked does not mean published."
    },
    {
      "title": "4. Publish results",
      "description": "Once everything checks out, click \"Publish\" — only a locked result can be published, and Publish is a separate button from Lock. Status moves to \"Published\", the card additionally shows who published it, and this result set becomes final and immutable; a correction requires calculating a new result set instead."
    }
  ],
  "afterward": [
    "Result status shows \"Published\"",
    "Participants can see their scores and rankings on their own competition page",
    "Every adjustment made before publishing is now final and cannot be changed"
  ],
  "nextSteps": "If a correction is needed after publishing, calculate a new result set (with a reason) rather than modifying the published one.",
  "faq": [
    {
      "q": "After calculating, some submissions are missing from the ranking — why?",
      "a": "This is expected, not a bug. A submission needs at least 2 judges' valid, locked scores to be included in a given result set; if only 1 (or 0) judges finished, or one of 2 judges was excluded due to a conflict of interest, that submission won't appear in the ranking. Check the Judge Assignments view to confirm assignment/completion status, then recalculate once it's complete."
    },
    {
      "q": "Can a published result still be edited?",
      "a": "No. A published result can no longer be changed; if a correction is genuinely needed, calculate a new result set instead of altering the published data."
    },
    {
      "q": "Can I still adjust award tiers after locking?",
      "a": "Yes. As long as the result hasn't been published yet (status \"Calculated\" or \"Locked\"), you can adjust an individual submission's award tier with a reason; once published, no further adjustment is possible."
    }
  ]
}
